import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'

// Hooks
import { useScrolledAnimation } from '../animation/useScrolledAnimation'

// Styles
import {
  AmenityMain,
  AmenityInner,
  AmenityLine,
  AmenityIcon,
  AmenityTitle,
  AmenityText,
} from './index.style'
import { TextBody, Title } from '../TextStyles'

const AmenitiesItem = ({
  id,
  as,
  iconSvg,
  title,
  text,
  bottomBorder = true,
  align = 'center',
  iconSize = 'large',
  compact = false,
}) => {
  const innerRef = useRef()
  const lineRef = useRef()
  const textRef = useRef()
  const amenityIn = useRef()

  useScrolledAnimation({
    id: id,
    transition: amenityIn,
    sync: false,
    useInView: true,
  })

  useEffect(() => {
    if (!innerRef.current) return

    amenityIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: 1.5,
        ease: 'power2.out',
      },
    })

    amenityIn.current.fromTo(
      innerRef.current,
      {
        opacity: 0,
        x: 50,
      },
      {
        opacity: 1,
        x: 0,
      },
      0
    )

    if (lineRef.current) {
      amenityIn.current.fromTo(
        lineRef.current,
        {
          opacity: 1,
          scaleX: 0,
        },
        {
          opacity: 0.2,
          scaleX: 1,
          ease: 'power2.inOut',
        },
        0
      )
    }

    if (textRef.current) {
      amenityIn.current.fromTo(
        textRef.current,
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0 },
        '<0.2'
      )
    }
  }, [innerRef, lineRef])

  return (
    <AmenityMain
      as={as}
      data-scroll
      data-scroll-id={id}
      align={align}
      compact={compact}
    >
      <AmenityInner ref={innerRef}>
        {iconSvg && (
          <AmenityIcon
            maxWidth={iconSvg.file.details.image.width}
            align={align}
            iconSize={iconSize}
          >
            <img src={iconSvg.file.url} alt={iconSvg.description} />
          </AmenityIcon>
        )}

        <AmenityTitle>
          <Title>{title}</Title>
        </AmenityTitle>
      </AmenityInner>

      {text && (
        <AmenityText ref={textRef} align={align}>
          <TextBody>{text.text}</TextBody>
        </AmenityText>
      )}

      {bottomBorder && <AmenityLine ref={lineRef} />}
    </AmenityMain>
  )
}

AmenitiesItem.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.object,
  title: PropTypes.string,
  bottomBorder: PropTypes.bool,
  compact: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  iconSize: PropTypes.oneOf(['small', 'large']),
}

export default AmenitiesItem
